import React from 'react';
import { Alert, AlertTitle, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'ErrorBoundary' });

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; retry: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    log.error('ErrorBoundary caught an error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} retry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  error?: Error;
  retry: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- Internal implementation, only ErrorBoundary is exported
const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, retry }) => (
  <Card>
    <CardContent>
      <Alert severity="error">
        <AlertTitle>Something went wrong</AlertTitle>
        <Stack spacing={2}>
          <Typography variant="body2">
            {error?.message || 'An unexpected error occurred. Please try again.'}
          </Typography>

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={retry} size="small">
            Try Again
          </Button>
        </Stack>
      </Alert>
    </CardContent>
  </Card>
);
